import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ProjectCard } from "../components/ProjectCard";
import type { Project } from "../../repositories/projectRepository";
import type { Build } from "../../repositories/buildRepository";

interface ProjectWithBuild extends Project {
  latestBuild?: Build | null;
}

export function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectWithBuild[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchProjects() {
      try {
        const response = await fetch("/api/projects", {
          credentials: "include",
        });

        if (!response.ok) {
          if (response.status === 401) {
            navigate("/login");
            return;
          }
          throw new Error("Failed to fetch projects");
        }

        const { data } = await response.json();

        // Fetch latest build for each project
        const projectsWithBuilds = await Promise.all(
          data.map(async (project: Project) => {
            try {
              const buildsRes = await fetch(`/api/projects/${project.id}/builds`, {
                credentials: "include",
              });
              if (buildsRes.ok) {
                const { data: builds } = await buildsRes.json();
                return { ...project, latestBuild: builds[0] || null };
              }
            } catch {
              // Ignore errors fetching builds
            }
            return { ...project, latestBuild: null };
          })
        );

        setProjects(projectsWithBuilds);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    }

    fetchProjects();
  }, [navigate]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto w-full px-6 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading projects...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto w-full px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">Projects</h1>
          <p className="text-muted-foreground mt-1">
            Manage your Godot game build pipelines
          </p>
        </div>
        <Button onClick={() => navigate("/projects/new")} className="shadow-lg shadow-primary/20">
          <span className="material-symbols-outlined text-[20px]">add</span>
          New Project
        </Button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg">
          {error}
        </div>
      )}

      {projects.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-xl border border-border">
          <span className="material-symbols-outlined text-6xl text-muted-foreground/30 mb-4 block">
            folder_open
          </span>
          <h2 className="text-xl font-semibold mb-2">No projects yet</h2>
          <p className="text-muted-foreground mb-6">
            Create your first project to start building your Godot games.
          </p>
          <Button onClick={() => navigate("/projects/new")} className="shadow-lg shadow-primary/20">
            <span className="material-symbols-outlined text-[20px]">add</span>
            Create Project
          </Button>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              latestBuild={project.latestBuild}
            />
          ))}
        </div>
      )}
    </div>
  );
}
